import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/store/configureStore';
import { store } from '../../app/store/configureStore';
import { OrderStatus } from '../../app/data/models/orders/orderStatus';
import { Order } from '../../app/data/models/orders/order';
import LoadingComponent from '../../app/layout/LoadingComponent';
import {
  fetchBillerOrdersAsync,
  observeOrderChange,
  closeOrderObserver,
  selectOrder,
  StreamConnectionStatus,
} from './billerOrdersSlice';
import api from '../../app/api/api';
import ListTitleBox from '../../app/components/list/ListTitleBox';
import { useTranslation } from 'react-i18next';
import OrderList from './components/OrderList';
import OrderDetailsContainer from './components/OrderDetailsContainer';
import PaymentActions from './components/PaymentActions';

import { Replay, Wifi, WifiOff } from '@mui/icons-material';
import { useRestaurant } from '@app/hooks/useRestaurant';

const BillerView: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { restaurant } = useRestaurant(restaurantId!);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Get data from Redux store
  const { orders, selectedOrder, isLoading, error, connectionStatus } =
    useAppSelector((state) => state.billerOrders);

  // Fetch orders when component mounts
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchBillerOrdersAsync({ restaurantId }));
    }
  }, [dispatch, restaurantId]);

  // Set up the event source for order notifications
  useEffect(() => {
    if (restaurantId) {
      const orderChangeThunk = observeOrderChange();
      dispatch(orderChangeThunk({ restaurantId }));
    }

    return () => {
      dispatch(closeOrderObserver());
    };
  }, [dispatch, restaurantId]);

  const handleOrderSelect = (order: Order) => {
    if (!order?.id) return;
    // Select the order from the local list only. Notifications will update the order details if needed.
    dispatch(selectOrder(order));
  };

  // Filter orders to show only those that are pending, processing, or ready
  const filteredOrders = Array.isArray(orders)
    ? orders.filter(
        (order) =>
          order.orderStatus === OrderStatus.Pending ||
          order.orderStatus === OrderStatus.Processing ||
          order.orderStatus === OrderStatus.Ready
      )
    : [];

  const handleReconnect = async () => {
    if (!restaurantId) return;

    try {
      // First close any existing connection
      dispatch(closeOrderObserver());

      // Try to refresh the token before reconnecting
      const user = store.getState().account.user;
      if (user && user.email && user.refreshToken) {
        console.log('Refreshing token before reconnecting...');

        // Call the token refresh API
        const response = await api.Account.refreshToken(
          user.email,
          user.refreshToken
        );

        if (response && response.accessToken) {
          console.log('Token refreshed successfully, reconnecting...');

          // Then attempt to reconnect with the restaurant ID
          dispatch(observeOrderChange()({ restaurantId }));
        } else {
          console.error('Failed to refresh token');
        }
      } else {
        // If no user or refresh token, just try to reconnect directly
        console.log(
          'No user or refresh token, attempting to reconnect directly...'
        );
        dispatch(observeOrderChange()({ restaurantId }));
      }
    } catch (error) {
      console.error('Error during reconnection:', error);

      // Even if token refresh fails, try to reconnect anyway
      // as the connection might still work with the existing token
      dispatch(observeOrderChange()({ restaurantId }));
    }
  };

  const ConnectionStatusIndicator = ({
    status,
    onReconnect,
  }: {
    status: StreamConnectionStatus;
    onReconnect: () => void;
  }) => {
    const [isReconnecting, setIsReconnecting] = useState(false);

    const handleReconnect = () => {
      setIsReconnecting(true);
      onReconnect();
      // Reset reconnecting state after a delay
      setTimeout(() => setIsReconnecting(false), 2000);
    };

    const getStatusDetails = () => {
      switch (status) {
        case StreamConnectionStatus.Connected:
          return {
            icon: <Wifi fontSize="small" />,
            label: t('kitchen.orders.connection.connected'),
            color: 'success',
            tooltip: t('kitchen.orders.connection.connected-tooltip'),
          };
        case StreamConnectionStatus.Disconnected:
          return {
            icon: <WifiOff fontSize="small" />,
            label: t('kitchen.orders.connection.disconnected'),
            color: 'error',
            tooltip: t('kitchen.orders.connection.disconnected-tooltip'),
          };
        default:
          return {
            icon: <WifiOff fontSize="small" />,
            label: t('kitchen.orders.connection.not-connected'),
            color: 'default',
            tooltip: t('kitchen.orders.connection.not-connected-tooltip'),
          };
      }
    };

    const statusDetails = getStatusDetails();

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={statusDetails.tooltip}>
          <Chip
            icon={statusDetails.icon}
            label={statusDetails.label}
            size="small"
            color={statusDetails.color as 'success' | 'error' | 'default'}
          />
        </Tooltip>

        {(status === StreamConnectionStatus.Disconnected ||
          status === StreamConnectionStatus.NotConnected) && (
          <Tooltip title={t('kitchen.orders.connection.reconnect')}>
            <IconButton
              size="small"
              color="primary"
              onClick={handleReconnect}
              disabled={isReconnecting}
              sx={{ ml: 0.5 }}
            >
              {isReconnecting ? (
                <CircularProgress size={20} />
              ) : (
                <Replay fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Create title element for ListTitleBox
  const titleElement = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'flex-start',
        width: '100%',
        gap: { xs: 0, sm: 1 },
      }}
    >
      <Typography variant="h4">{t('biller.title')}</Typography>
      <ConnectionStatusIndicator
        status={connectionStatus}
        onReconnect={handleReconnect}
      />
    </Box>
  );

  // Local state for manual refresh spinner
  const [refreshing, setRefreshing] = useState(false);

  const handleForceRefresh = async () => {
    if (!restaurantId) return;
    setRefreshing(true);
    try {
      await dispatch(fetchBillerOrdersAsync({ restaurantId }));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ListTitleBox
          title={titleElement}
          primaryButtonText={t('common.refresh')}
          primaryButtonProps={{
            disabled: refreshing,
            startIcon: refreshing ? (
              <CircularProgress size={16} />
            ) : (
              <Replay fontSize="small" />
            ),
            variant: 'contained',
            color: 'primary',
          }}
          onPrimaryClick={() => {
            handleForceRefresh();
          }}
        />
      </Box>
      {isLoading ? (
        <LoadingComponent />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5} lg={4} xl={3}>
            <Paper sx={{ height: '100%', p: 1 }}>
              {error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <OrderList
                  key={orders.map((o) => o.id).join(',')}
                  restaurantId={restaurantId}
                  onOrderSelect={handleOrderSelect}
                  preloadedOrders={filteredOrders}
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={7} lg={8} xl={9}>
            {selectedOrder && selectedOrder.id && restaurantId ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ height: '100%', p: 1 }}>
                    <OrderDetailsContainer
                      restaurantId={restaurantId}
                      orderId={selectedOrder.id.toString()}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ height: '100%', p: 1 }}>
                    <PaymentActions
                      order={selectedOrder}
                      restaurant={restaurant}
                      restaurantId={restaurantId}
                      onOrderUpdated={undefined} // Notification-driven update: do not fetch immediately
                    />
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Paper sx={{ height: '100%', p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  {t('biller.no-orders-selected')}
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BillerView;
